/**
 * "Add to List" gate on product cards. When a `.add-to-list` button is clicked:
 *  - logged-out visitor  → prompt to log in first (login / cancel);
 *  - logged-in visitor   → show a modal checklist of the company's saved
 *                          lists (with a per-list quantity) and save the
 *                          variant into every list that's checked.
 *
 * Login state + strings come from globals set in theme.liquid
 * (window.customerLoggedIn, window.loginPrompt, window.addToListPrompt).
 * The company's saved lists are read from the backend's /order-list GET (same
 * shape the Your Lists page reads) and re-read before every write.
 *
 * The overlay shell, the login prompt and the saved-lists cache are exported for
 * saveCartToList.js, which puts the whole cart into a new list from the cart
 * page: same modal, same lists, same slot metafields.
 */

import {
  findFreeSlotIndex,
  generateListId,
  getOrderLists,
  indexFromMetafieldKey,
  metafieldKeyFromIndex,
  putOrderList,
  sortListsByLastUpdated,
} from '../objects/your-lists/orderListApi';

const TRANSITION_MS = 250;
const TOAST_DURATION_MS = 6000;

let overlayEl = null;
let toastEl = null;
let toastTimer = null;

function onKey(e) {
  if (e.key === 'Escape') closeModal();
}

export function closeModal() {
  if (!overlayEl) return;
  const el = overlayEl;
  overlayEl = null;
  el.classList.remove('is-open');
  document.removeEventListener('keydown', onKey);
  document.body.style.overflow = '';
  setTimeout(() => el.remove(), TRANSITION_MS);
}

/**
 * Mount an overlay with the given inner markup, wire up close-on-backdrop /
 * close-on-Esc / scroll lock, and run the enter transition. Returns the overlay
 * element so callers can query and bind to its contents.
 */
export function mountOverlay(className, innerHTML) {
  if (overlayEl) return null;

  overlayEl = document.createElement('div');
  overlayEl.className = className;
  overlayEl.innerHTML = innerHTML;

  document.body.appendChild(overlayEl);
  document.body.style.overflow = 'hidden';

  overlayEl.addEventListener('click', (e) => {
    if (e.target === overlayEl) closeModal();
  });
  document.addEventListener('keydown', onKey);

  requestAnimationFrame(() => overlayEl && overlayEl.classList.add('is-open'));

  return overlayEl;
}

/* -------------------------------------------------------------------------- */
/* "Saved to list(s)" toast — shown top-right after the drawer closes         */
/* -------------------------------------------------------------------------- */

function closeToast() {
  if (!toastEl) return;
  const el = toastEl;
  toastEl = null;
  if (toastTimer) {
    clearTimeout(toastTimer);
    toastTimer = null;
  }
  el.classList.remove('is-open');
  setTimeout(() => el.remove(), TRANSITION_MS);
}

/** Join HTML fragments as "a", "a and b", or "a, b and c". */
function joinWithAnd(parts, and) {
  if (parts.length <= 1) return parts.join('');
  return `${parts.slice(0, -1).join(', ')} ${and} ${parts[parts.length - 1]}`;
}

/**
 * Toast shown top-right once the drawer has closed after a successful save —
 * names the list(s) the item landed in, with quick links to edit the first
 * one or view the Your Lists dashboard.
 */
function showSavedToast(p, btn, savedLists, yourListsUrl) {
  closeToast();
  if (!savedLists.length) return;

  const image = btn.dataset.productImage || '';
  const title = buildItemTitle(btn) || btn.dataset.productTitle || '';
  const listHref = (list) => {
    const sep = yourListsUrl.includes('?') ? '&' : '?';
    return `${yourListsUrl}${sep}list=${encodeURIComponent(list.id)}`;
  };
  const listLinks = savedLists.map(
    (list) => `<a href="${listHref(list)}">${escapeHtml(list.name || '')}</a>`,
  );

  toastEl = document.createElement('div');
  toastEl.className = 'mr-list-toast';
  toastEl.innerHTML = `
    <div class="mr-list-toast-top" role="status" aria-live="polite">
      ${image ? `<img class="mr-list-toast-image" src="${escapeHtml(image)}" alt="" aria-hidden="true">` : ''}
      <div class="mr-list-toast-text">
        <div class="mr-list-toast-title">${escapeHtml(title)}</div>
        <div class="mr-list-toast-message">${escapeHtml(p.savedTo || 'has been saved to')} ${joinWithAnd(listLinks, escapeHtml(p.and || 'and'))}</div>
      </div>
      <div class="mr-list-toast-actions">
        <a class="mr-list-toast-edit" href="${listHref(savedLists[0])}">${escapeHtml(p.editList || 'Edit List')}</a>
        <a class="mr-list-toast-view" href="${escapeHtml(yourListsUrl)}">${escapeHtml(p.viewLists || 'View Lists')}</a>
      </div>
    </div>`;

  document.body.appendChild(toastEl);
  requestAnimationFrame(() => toastEl && toastEl.classList.add('is-open'));

  toastTimer = setTimeout(closeToast, TOAST_DURATION_MS);
}

/* -------------------------------------------------------------------------- */
/* Logged-out: login prompt                                                   */
/* -------------------------------------------------------------------------- */

export function openLoginModal() {
  const lp = window.loginPrompt || {};

  const overlay = mountOverlay(
    'mr-login-modal-overlay',
    `
    <div class="mr-login-modal" role="dialog" aria-modal="true">
      <button type="button" class="mr-login-modal-close" aria-label="${lp.cancel || 'Cancel'}">
        <svg viewBox="0 0 14 14" width="14" height="14" fill="none" aria-hidden="true">
          <path d="M1 1 L13 13 M13 1 L1 13" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
        </svg>
      </button>
      <div class="mr-login-modal-title">${lp.title || 'Please log in'}</div>
      <div class="mr-login-modal-actions">
        <button type="button" class="mr-login-modal-login">${lp.login || 'Log in'}</button>
        <button type="button" class="mr-login-modal-cancel">${lp.cancel || 'Cancel'}</button>
      </div>
    </div>`,
  );
  if (!overlay) return;

  overlay
    .querySelector('.mr-login-modal-close')
    .addEventListener('click', closeModal);
  overlay
    .querySelector('.mr-login-modal-cancel')
    .addEventListener('click', closeModal);
  overlay
    .querySelector('.mr-login-modal-login')
    .addEventListener('click', () => {
      window.location.href = lp.loginUrl || '/account/login';
    });
}

/* -------------------------------------------------------------------------- */
/* Logged-in: pick a list                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Fetch + cache the company's saved lists on demand, from the backend's
 * /order-list GET (Admin API) rather than Liquid — see getOrderLists() for why
 * (COB2B-785). The cache only drives the add-to-list button icons; anything
 * that writes passes `{ fresh: true }` so it builds on the current server copy.
 */
let listsCache = null;
let listsPromise = null;

export function fetchSavedLists({ fresh = false } = {}) {
  if (listsCache && !fresh) return Promise.resolve(listsCache);
  if (!listsPromise || fresh) {
    const promise = getOrderLists()
      .then((lists) => {
        // An older request finishing late mustn't replace a newer result.
        if (listsPromise === promise) listsCache = lists;
        return lists;
      })
      .catch((err) => {
        if (listsPromise === promise) listsPromise = null; // allow a retry on the next open
        throw err;
      });
    listsPromise = promise;
  }
  return listsPromise;
}

/** All variant ids (as strings) present in any item across the given lists. */
function variantIdsInLists(lists) {
  const ids = new Set();
  (lists || []).forEach((list) => {
    (Array.isArray(list.items) ? list.items : []).forEach((item) => {
      ids.add(String(item.id));
    });
  });
  return ids;
}

/**
 * Swap every `.add-to-list` button on the page between its inactive/active
 * icon, based on whether the variant it targets is already saved in any of
 * the company's lists. `.swym-added` is the class that already drives that
 * swap (scss/components/_productCard.scss and friends — a name left over
 * from the earlier Swym integration, reused here rather than adding a
 * parallel class). Safe to call before the lists have loaded (no-ops).
 */
function refreshAddToListButtons(root) {
  if (!listsCache) return;
  const savedVariantIds = variantIdsInLists(listsCache);
  (root || document).querySelectorAll('.add-to-list').forEach((el) => {
    const variantId = String(el.dataset.variantId || '');
    el.classList.toggle(
      'swym-added',
      Boolean(variantId) && savedVariantIds.has(variantId),
    );
  });
}

// Picks up `.add-to-list` buttons rendered after the initial page load (PLP
// filtering/pagination, quick-view modals, …) so their icon starts correct
// too, instead of every such render path having to remember to call
// refreshAddToListButtons() itself.
let addToListObserver = null;
function observeAddToListButtons() {
  if (addToListObserver || typeof MutationObserver === 'undefined') return;
  let pending = null;
  addToListObserver = new MutationObserver(() => {
    if (pending) return;
    pending = setTimeout(() => {
      pending = null;
      refreshAddToListButtons();
    }, 50);
  });
  addToListObserver.observe(document.body, { childList: true, subtree: true });
}

/** Build the item title stored on the list (product, plus variant if meaningful). */
function buildItemTitle(btn) {
  const product = btn.dataset.productTitle || '';
  const variant = btn.dataset.variantTitle || '';
  if (variant && variant !== 'Default Title') {
    return product ? `${product} - ${variant}` : variant;
  }
  return product;
}

/** Escape text dropped into an innerHTML template (list/product names are free text). */
function escapeHtml(value) {
  return String(value == null ? '' : value).replace(
    /[&<>"']/g,
    (ch) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[
        ch
      ],
  );
}

/**
 * Persist a single list to its own company metafield (order_list.order_list_N).
 * `list` carries `metafieldKey` (the slot); the stored value omits it.
 */
async function persistList(list) {
  const { metafieldKey, ...value } = list;
  const index = indexFromMetafieldKey(metafieldKey);
  if (!index) {
    throw new Error(
      `persistList: missing/invalid metafieldKey on list ${list.id}`,
    );
  }
  await putOrderList(index, value);
}

/**
 * True while `overlay` is still the mounted one — worth checking after an await,
 * since the customer can close the modal while a request is in flight.
 */
export function isCurrentOverlay(overlay) {
  return overlayEl === overlay;
}

/**
 * Replace the cached lists after something outside this module has written one,
 * so the next picker opens with the new list in it instead of a stale set.
 */
export function primeSavedLists(lists) {
  listsCache = Array.isArray(lists) ? lists : null;
  refreshAddToListButtons();
}

/** Set a status message's text and tone ('success' | 'error' | neutral). */
export function setMessageTone(el, text, tone) {
  el.textContent = text;
  el.classList.toggle('is-success', tone === 'success');
  el.classList.toggle('is-error', tone === 'error');
}

/** Replace the modal body markup (the shell + close button stay). */
export function setModalBody(overlay, html) {
  const body = overlay.querySelector('.mr-list-modal-body');
  if (body) body.innerHTML = html;
}

/** Small product row shown at the top of the modal — what's being added. */
function productHeaderHtml(p, btn) {
  const image = btn.dataset.productImage || '';
  const title = buildItemTitle(btn) || btn.dataset.productTitle || '';
  return `
    <div class="mr-list-modal-product">
      ${image ? `<img class="mr-list-modal-product-image" src="${escapeHtml(image)}" alt="" aria-hidden="true">` : ''}
      <div class="mr-list-modal-product-title">${escapeHtml(title)}</div>
    </div>`;
}

async function openListModal(btn) {
  const p = window.addToListPrompt || {};
  const variantId = String(btn.dataset.variantId || '');
  if (!variantId) return;

  // Open immediately with a loading state, then fill in once the lists load.
  // Slides in from the right like the mini-cart — a dedicated overlay class
  // (mr-list-drawer-overlay) keeps this from affecting the centered
  // login-prompt / save-cart modals that share the .mr-list-modal markup.
  const overlay = mountOverlay(
    'mr-list-drawer-overlay',
    `
    <div class="mr-list-modal" role="dialog" aria-modal="true">
      <button type="button" class="mr-login-modal-close" aria-label="${p.cancel || 'Cancel'}">
        <svg width="28" height="11" viewBox="0 0 28 11" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4.31661e-07 4.93763L25.8569 4.93763L21.7327 0.795195L22.5246 4.78674e-07L28 5.5L22.5246 11L21.7327 10.2048L25.8569 6.06237L5.29989e-07 6.06238L4.31661e-07 4.93763Z" fill="black"/>
        </svg>
      </button>
      <div class="mr-list-modal-title">${p.title || 'Save To List'}</div>
      <div class="mr-list-modal-body">
        ${productHeaderHtml(p, btn)}
        <div class="mr-list-modal-loading">${p.loading || 'Loading…'}</div>
      </div>
    </div>`,
  );
  if (!overlay) return;
  overlay
    .querySelector('.mr-login-modal-close')
    .addEventListener('click', closeModal);

  let lists;
  try {
    // Always the current server copy: the picker pre-checks from it and Save
    // writes on top of it (COB2B-785).
    lists = await fetchSavedLists({ fresh: true });
  } catch (e) {
    if (overlayEl !== overlay) return; // closed while loading
    setModalBody(
      overlay,
      `${productHeaderHtml(p, btn)}
       <div class="mr-list-modal-message is-error">${p.error || 'Something went wrong. Please try again.'}</div>
       <div class="mr-list-modal-actions">
         <button type="button" class="mr-list-modal-cancel">${p.cancel || 'Cancel'}</button>
       </div>`,
    );
    overlay
      .querySelector('.mr-list-modal-cancel')
      .addEventListener('click', closeModal);
    return;
  }

  if (overlayEl !== overlay) return; // closed while loading

  renderListPicker(overlay, p, lists, btn, variantId);
}

/** One list row: checkbox + name + qty stepper (disabled until checked) + View List. */
function renderRow(list, p, yourListsUrl) {
  const id = String(list.id);
  const separator = yourListsUrl.includes('?') ? '&' : '?';
  return `
    <div class="mr-list-modal-row" data-list-id="${escapeHtml(id)}">
      <label class="mr-list-modal-check">
        <input type="checkbox" class="mr-list-modal-check-input" aria-label="${escapeHtml(p.selectList || 'Select list')}">
        <span class="mr-list-modal-check-box">
          <svg viewBox="0 0 14 11" width="14" height="11" fill="none" aria-hidden="true">
            <path d="M1 5.5 5 9.5 13 1" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
      </label>
      <span class="mr-list-modal-name">${escapeHtml(list.name || '')}</span>
      <div class="mr-list-modal-qty">
        <button type="button" class="mr-list-modal-qty-btn mr-list-modal-qty-minus" aria-label="${escapeHtml(p.decreaseQuantity || 'Decrease quantity')}" disabled></button>
        <input type="number" class="mr-list-modal-qty-value" min="1" value="0" disabled aria-label="${escapeHtml(p.quantity || 'Quantity')}">
        <button type="button" class="mr-list-modal-qty-btn mr-list-modal-qty-plus" aria-label="${escapeHtml(p.increaseQuantity || 'Increase quantity')}" disabled></button>
      </div>
      <a class="mr-list-modal-viewlist" href="${yourListsUrl}${separator}list=${encodeURIComponent(id)}">${escapeHtml(p.viewList || 'View List')}</a>
    </div>`;
}

/** Wire a rendered row's checkbox + stepper to `selections`, keeping them in sync. */
function wireRow(rowEl, list, selections, updateSaveEnabled) {
  const id = String(list.id);
  const checkbox = rowEl.querySelector('.mr-list-modal-check-input');
  const nameEl = rowEl.querySelector('.mr-list-modal-name');
  const qtyWrap = rowEl.querySelector('.mr-list-modal-qty');
  const qtyInput = rowEl.querySelector('.mr-list-modal-qty-value');
  const minusBtn = rowEl.querySelector('.mr-list-modal-qty-minus');
  const plusBtn = rowEl.querySelector('.mr-list-modal-qty-plus');

  const clamp = (n) => {
    const v = parseInt(n, 10);
    return Number.isFinite(v) && v > 1 ? v : 1;
  };

  const setQty = (n) => {
    const qty = clamp(n);
    selections.get(id).qty = qty;
    qtyInput.value = String(qty);
    minusBtn.disabled = qty <= 1;
    updateSaveEnabled();
  };

  const setChecked = (checked) => {
    const sel = selections.get(id);
    sel.checked = checked;
    sel.qty = checked ? Math.max(1, sel.qty || 1) : 0;
    checkbox.checked = checked;
    nameEl.classList.toggle('is-checked', checked);
    qtyWrap.classList.toggle('is-active', checked);
    qtyInput.value = String(sel.qty);
    qtyInput.disabled = !checked;
    minusBtn.disabled = !checked || sel.qty <= 1;
    plusBtn.disabled = !checked;
    updateSaveEnabled();
  };

  checkbox.addEventListener('change', () => setChecked(checkbox.checked));
  // The checkbox itself toggles via its wrapping <label> (mr-list-modal-check);
  // the name sits outside that label, so forward its clicks the same way.
  nameEl.addEventListener('click', () => checkbox.click());
  minusBtn.addEventListener('click', () =>
    setQty((selections.get(id).qty || 1) - 1),
  );
  plusBtn.addEventListener('click', () =>
    setQty((selections.get(id).qty || 1) + 1),
  );
  qtyInput.addEventListener('input', () => setQty(qtyInput.value));

  return { setChecked };
}

function createLinkHtml(p) {
  return `
    <button type="button" class="mr-list-modal-createlink">
      <span class="mr-list-modal-createlink-icon" aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g>
            <mask id="path-1-outside-1_1872_14247" maskUnits="userSpaceOnUse" x="-0.800049" y="-0.800049" width="19" height="19" fill="currentColor">
              <rect fill="white" x="-0.800049" y="-0.800049" width="19" height="19"/>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M9.19995 8.19995V0.199951H8.19995L8.19995 8.19995H0.199951V9.19995H8.19995L8.19995 17.2H9.19995V9.19995H17.2V8.19995H9.19995Z"/>
            </mask>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M9.19995 8.19995V0.199951H8.19995L8.19995 8.19995H0.199951V9.19995H8.19995L8.19995 17.2H9.19995V9.19995H17.2V8.19995H9.19995Z" fill="currentColor"/>
            <path d="M9.19995 0.199951H9.39995V-4.88311e-05H9.19995V0.199951ZM9.19995 8.19995H8.99995V8.39995H9.19995V8.19995ZM8.19995 0.199951V-4.88311e-05H7.99995V0.199951L8.19995 0.199951ZM8.19995 8.19995V8.39995H8.39995V8.19995H8.19995ZM0.199951 8.19995V7.99995H-4.88311e-05V8.19995H0.199951ZM0.199951 9.19995H-4.88311e-05V9.39995H0.199951V9.19995ZM8.19995 9.19995H8.39995V8.99995H8.19995V9.19995ZM8.19995 17.2H7.99995V17.4H8.19995V17.2ZM9.19995 17.2V17.4H9.39995V17.2H9.19995ZM9.19995 9.19995V8.99995H8.99995V9.19995H9.19995ZM17.2 9.19995V9.39995H17.4V9.19995H17.2ZM17.2 8.19995H17.4V7.99995H17.2V8.19995ZM8.99995 0.199951V8.19995H9.39995V0.199951H8.99995ZM8.19995 0.399951H9.19995V-4.88311e-05H8.19995V0.399951ZM8.39995 8.19995V0.199951L7.99995 0.199951L7.99995 8.19995H8.39995ZM0.199951 8.39995H8.19995V7.99995H0.199951V8.39995ZM0.399951 9.19995V8.19995H-4.88311e-05V9.19995H0.399951ZM8.19995 8.99995H0.199951V9.39995H8.19995V8.99995ZM8.39995 17.2L8.39995 9.19995H7.99995L7.99995 17.2H8.39995ZM9.19995 17H8.19995V17.4H9.19995V17ZM8.99995 9.19995V17.2H9.39995V9.19995H8.99995ZM17.2 8.99995H9.19995V9.39995H17.2V8.99995ZM17 8.19995V9.19995H17.4V8.19995H17ZM9.19995 8.39995H17.2V7.99995H9.19995V8.39995Z" fill="currentColor" mask="url(#path-1-outside-1_1872_14247)"/>
          </g>
        </svg>
      </span>
      ${escapeHtml(p.createNewList || 'Create A New List')}
    </button>`;
}

/** The "Enter New List Name" block — sits in the scrollable body, above the footer bar. */
function newlistFormHtml(p) {
  return `
    <div class="mr-list-modal-newlist">
      <div class="mr-list-modal-newlist-label">${escapeHtml(p.enterListName || 'Enter New List Name')}</div>
      <div class="mr-list-modal-newlist-row">
        <input
          type="text"
          class="mr-list-modal-input mr-list-modal-newlist-input"
          maxlength="60"
          placeholder="${escapeHtml(p.namePlaceholder || 'List Name')}"
          aria-label="${escapeHtml(p.enterListName || 'Enter New List Name')}"
        >
        <button type="button" class="mr-list-modal-save mr-list-modal-newlist-save" disabled>${escapeHtml(p.saveNewList || 'Save New List')}</button>
      </div>
      <div class="mr-list-modal-message mr-list-modal-newlist-message" role="status" aria-live="polite"></div>
    </div>`;
}

/** The footer-bar link shown while the "create a list" form is open. */
function cancelLinkHtml(p) {
  return `
    <button type="button" class="mr-list-modal-createlink mr-list-modal-cancel-createlink">
      <span class="mr-list-modal-createlink-icon" aria-hidden="true">
        <svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="-0.142967" width="11.6967" height="0.80509" rx="0.402545" transform="matrix(0.714834 -0.699294 0.714834 0.699294 0.0346702 8.01349)" fill="currentColor" stroke="currentColor" stroke-width="0.2"/>
          <rect x="1.19209e-08" y="0.139859" width="11.6967" height="0.80509" rx="0.402545" transform="matrix(-0.714834 -0.699294 0.714834 -0.699294 8.19397 8.77453)" fill="currentColor" stroke="currentColor" stroke-width="0.2"/>
        </svg>
      </span>
      ${escapeHtml(p.cancelNewList || 'Cancel New List')}
    </button>`;
}

/** Render the list checklist + inline "create a list" + Save To List into the (already open) modal. */
function renderListPicker(overlay, p, initialLists, btn, variantId) {
  const yourListsUrl = p.yourListsUrl || '/pages/your-lists';
  // Most recently updated first, same order as the Your Lists page.
  const lists = sortListsByLastUpdated(initialLists);
  // Pre-check whatever list(s) already hold this variant, at its saved
  // quantity — reopening the drawer shows the real current state, and
  // unchecking one of these below is how the customer removes it from that
  // list (see the Save handler).
  const selections = new Map(
    lists.map((list) => {
      const items = Array.isArray(list.items) ? list.items : [];
      const existing = items.find((it) => String(it.id) === variantId);
      return [
        String(list.id),
        existing
          ? { checked: true, qty: existing.quantity || 1 }
          : { checked: false, qty: 0 },
      ];
    }),
  );
  // Snapshot at open time so Save can tell what actually changed — a
  // newly-checked list is added to, an originally-checked one that's since
  // been unchecked is removed from, and anything untouched is left alone.
  const initialSelections = new Map(
    [...selections.entries()].map(([id, sel]) => [id, { ...sel }]),
  );

  setModalBody(
    overlay,
    `${productHeaderHtml(p, btn)}
     <div class="mr-list-modal-rows"></div>
     <div class="mr-list-modal-newlist-slot"></div>
     <div class="mr-list-modal-message mr-list-modal-status" role="status" aria-live="polite"></div>
     <div class="mr-list-modal-footer">
       <div class="mr-list-modal-footer-left"></div>
       <button type="button" class="mr-list-modal-save mr-list-modal-submit" disabled>${escapeHtml(p.save || 'Save To List')}</button>
     </div>`,
  );

  const rowsContainer = overlay.querySelector('.mr-list-modal-rows');
  const newlistSlot = overlay.querySelector('.mr-list-modal-newlist-slot');
  const footerLeft = overlay.querySelector('.mr-list-modal-footer-left');
  const saveBtn = overlay.querySelector('.mr-list-modal-submit');
  const message = overlay.querySelector('.mr-list-modal-status');

  // Enabled iff something actually differs from how the drawer was opened —
  // a check, an uncheck (= a pending removal), or a quantity edit.
  const isDirty = () => {
    for (const [id, sel] of selections.entries()) {
      const initial = initialSelections.get(id) || { checked: false, qty: 0 };
      if (sel.checked !== initial.checked) return true;
      if (sel.checked && sel.qty !== initial.qty) return true;
    }
    return false;
  };
  const updateSaveEnabled = () => {
    saveBtn.disabled = !isDirty();
  };

  // A list created in the drawer is the newest, so it goes on top.
  const addRow = (list, { prepend = false } = {}) => {
    rowsContainer.insertAdjacentHTML(
      prepend ? 'afterbegin' : 'beforeend',
      renderRow(list, p, yourListsUrl),
    );
    const rowEl = prepend
      ? rowsContainer.firstElementChild
      : rowsContainer.lastElementChild;
    return wireRow(rowEl, list, selections, updateSaveEnabled);
  };

  // Rows for lists that already hold this variant open pre-checked, at the
  // quantity seeded into `selections` above.
  lists.forEach((list) => {
    const ref = addRow(list);
    if (selections.get(String(list.id)).checked) ref.setChecked(true);
  });

  function showCreateLink() {
    newlistSlot.innerHTML = '';
    footerLeft.innerHTML = createLinkHtml(p);
    footerLeft
      .querySelector('.mr-list-modal-createlink')
      .addEventListener('click', showCreateForm);
  }

  function showCreateForm() {
    newlistSlot.innerHTML = newlistFormHtml(p);
    footerLeft.innerHTML = cancelLinkHtml(p);

    const input = newlistSlot.querySelector('.mr-list-modal-newlist-input');
    const newSaveBtn = newlistSlot.querySelector('.mr-list-modal-newlist-save');
    const newMessage = newlistSlot.querySelector(
      '.mr-list-modal-newlist-message',
    );
    const cancelBtn = footerLeft.querySelector(
      '.mr-list-modal-cancel-createlink',
    );

    input.addEventListener('input', () => {
      newSaveBtn.disabled = !input.value.trim();
      setMessageTone(newMessage, '');
    });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && !newSaveBtn.disabled) newSaveBtn.click();
    });
    cancelBtn.addEventListener('click', showCreateLink);

    newSaveBtn.addEventListener('click', async () => {
      const name = input.value.trim();
      if (!name || newSaveBtn.disabled) return;

      newSaveBtn.disabled = true;
      input.disabled = true;
      newSaveBtn.textContent = p.saving || 'Saving…';
      setMessageTone(newMessage, '');

      const restoreForm = (text) => {
        newSaveBtn.disabled = false;
        input.disabled = false;
        newSaveBtn.textContent = p.saveNewList || 'Save New List';
        setMessageTone(newMessage, text, 'error');
      };

      const newList = {
        id: generateListId(),
        name,
        items: [],
        createdAt: new Date().toISOString(),
      };

      try {
        // Pick the slot from the server copy, not what the drawer opened with —
        // a slot that only looks free would overwrite a list (COB2B-785).
        const current = await fetchSavedLists({ fresh: true });
        if (!isCurrentOverlay(overlay)) return;
        const freeIndex = findFreeSlotIndex(current);
        if (freeIndex === null) {
          restoreForm(
            p.limitReached || 'You have reached the maximum number of lists.',
          );
          return;
        }

        await putOrderList(freeIndex, newList);
        if (!isCurrentOverlay(overlay)) return;

        const stored = {
          ...newList,
          metafieldKey: metafieldKeyFromIndex(freeIndex),
        };
        listsCache = [...current, stored];
        lists.unshift(stored);
        selections.set(String(stored.id), { checked: false, qty: 0 });

        const row = addRow(stored, { prepend: true });
        row.setChecked(true); // new list starts pre-selected at qty 1

        showCreateLink();
      } catch (err) {
        if (!isCurrentOverlay(overlay)) return;
        restoreForm(p.error || 'Something went wrong. Please try again.');
      }
    });
  }

  if (lists.length === 0) {
    showCreateForm();
  } else {
    showCreateLink();
  }

  saveBtn.addEventListener('click', async () => {
    if (saveBtn.disabled) return;

    // Only lists whose checked-state or quantity actually differs from how
    // the drawer opened get written — everything else is left untouched.
    // A list newly checked (or already-checked with a new quantity) is
    // upserted; one that was checked at open time and got unchecked since
    // has the item removed from it instead.
    const pending = [];
    selections.forEach((sel, id) => {
      const initial = initialSelections.get(id) || { checked: false, qty: 0 };
      const unchanged =
        sel.checked === initial.checked &&
        (!sel.checked || sel.qty === initial.qty);
      if (!unchanged) pending.push([id, sel]);
    });

    if (!pending.length) {
      closeModal();
      return;
    }

    saveBtn.disabled = true;
    saveBtn.textContent = p.saving || 'Saving…';
    setMessageTone(message, '');

    // Each PUT replaces the whole list, so apply this one variant's change to
    // the current server copy — never to what the drawer opened with, or items
    // saved since (another tab, another product) get wiped (COB2B-785).
    let current;
    try {
      current = await fetchSavedLists({ fresh: true });
    } catch (err) {
      if (!isCurrentOverlay(overlay)) return;
      saveBtn.disabled = false;
      saveBtn.textContent = p.save || 'Save To List';
      setMessageTone(
        message,
        p.error || 'Something went wrong. Please try again.',
        'error',
      );
      return;
    }
    if (!isCurrentOverlay(overlay)) return;

    const changes = [];
    pending.forEach(([id, sel]) => {
      const target = current.find((l) => String(l.id) === id);
      if (!target) return;
      const items = Array.isArray(target.items) ? target.items : [];
      const existingIndex = items.findIndex(
        (it) => String(it.id) === variantId,
      );

      let nextItems;
      if (sel.checked) {
        const qty = sel.qty || 1;
        nextItems =
          existingIndex >= 0
            ? items.map((it, i) =>
                i === existingIndex ? { ...it, quantity: qty } : it,
              )
            : [
                ...items,
                {
                  id: variantId,
                  title: buildItemTitle(btn),
                  quantity: qty,
                  selected: false,
                },
              ];
      } else {
        nextItems = items.filter((_, i) => i !== existingIndex);
      }

      changes.push({
        list: {
          ...target,
          items: nextItems,
          updatedAt: new Date().toISOString(),
        },
        action: sel.checked ? 'save' : 'remove',
      });
    });

    if (!changes.length) {
      // Every list touched was deleted elsewhere in the meantime.
      closeModal();
      return;
    }

    const results = await Promise.allSettled(
      changes.map((change) => persistList(change.list)),
    );
    if (!isCurrentOverlay(overlay)) return;

    const failedNames = [];
    changes.forEach((change, i) => {
      if (results[i].status === 'fulfilled') {
        listsCache = (listsCache || current).map((l) =>
          String(l.id) === String(change.list.id) ? change.list : l,
        );
      } else {
        failedNames.push(change.list.name || '');
      }
    });
    refreshAddToListButtons();

    if (!failedNames.length) {
      closeModal();
      const savedLists = changes
        .filter((change) => change.action === 'save')
        .map((change) => change.list);
      showSavedToast(p, btn, savedLists, yourListsUrl);
    } else {
      saveBtn.disabled = false;
      saveBtn.textContent = p.save || 'Save To List';
      const text =
        failedNames.length === changes.length
          ? p.error || 'Something went wrong. Please try again.'
          : `${p.partialError || "Some lists couldn't be saved. Please try again."} (${failedNames.join(', ')})`;
      setMessageTone(message, text, 'error');
    }
  });
}

const addToListLogin = () => {
  // Prime the active/inactive icon on every .add-to-list button up front —
  // otherwise it would only ever be known once someone opens the drawer.
  if (window.customerLoggedIn) {
    fetchSavedLists()
      .then(() => refreshAddToListButtons())
      .catch(() => {}); // icon just stays inactive; the drawer will retry
    observeAddToListButtons();

    // Coming Back to a PLP/PDP can restore it from the bfcache, JS state and
    // all — icons from before items were removed/added on Your Lists would
    // stick (COB2B-785). Re-read them from the server.
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      fetchSavedLists({ fresh: true })
        .then(() => refreshAddToListButtons())
        .catch(() => {});
    });
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.add-to-list');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    if (!window.customerLoggedIn) {
      openLoginModal();
    } else {
      openListModal(btn);
    }
  });
};

export default addToListLogin;
